import { EmailType } from 'broker/components/EmailEditor/store/types';
import useEmailEditorActions from 'broker/components/EmailEditor/store/useEmailEditorActions';
import useEmailEditorState from 'broker/components/EmailEditor/store/useEmailEditorState';
import { RecipientsGroupsAvailableRecipients } from 'broker/components/EmailEditor/types';
import { Recipient, RecipientGroup } from 'broker/components/Emails/recipient-utils';
import RecipientGroupTag from 'broker/components/Emails/RecipientGroupTag';
import { DraftEditorMode } from 'broker/components/Emails/useDraftEditorMode';
import { useApplyDirtyChanges } from './useApplyDirtyChanges';

interface RecipientGroupChipProps {
  recipientGroup: RecipientGroup;
  selected?: boolean;
  onClick?: (recipientGroupId: string) => void;
  icon?: JSX.Element | undefined;
  recipientGroupsAvailableContacts: RecipientsGroupsAvailableRecipients;
  isReadOnly?: boolean;
}

export const RecipientGroupChip = ({
  recipientGroup,
  selected,
  onClick,
  icon,
  recipientGroupsAvailableContacts,
  isReadOnly,
}: RecipientGroupChipProps) => {
  const {
    emailType,
    draftEditorMode,
    recipients: { newRecipients },
  } = useEmailEditorState();

  const {
    addCustomerContact,
    addMarketContact,
    addInsuredContact,
    recipients: { clearNewRecipients, toggleRecipient, removeRecipientGroup },
    draftEditing: { resetDirtyState },
  } = useEmailEditorActions();

  const { applyChanges } = useApplyDirtyChanges();

  const handleDelete = () => {
    applyChanges();
    removeRecipientGroup(recipientGroup.id);
  };

  const handleClick = () => {
    applyChanges();
    onClick?.(recipientGroup.id);
  };

  const handleAddContact = () => {
    switch (emailType) {
      case EmailType.Retailer:
        return addCustomerContact;
      case EmailType.Insured:
        return addInsuredContact;
      case EmailType.Market:
      default:
        return addMarketContact;
    }
  };

  return (
    <RecipientGroupTag
      isNewEmailEditor
      recipientGroup={recipientGroup}
      key={recipientGroup.id}
      availableRecipients={recipientGroupsAvailableContacts[recipientGroup.id] ?? []}
      selected={selected}
      icon={icon}
      newRecipients={newRecipients ?? []}
      onClick={handleClick}
      onClearNewRecipients={clearNewRecipients}
      onAddRecipient={handleAddContact()}
      onToggleRecipient={(recipient: Recipient, removeIfLast?: boolean) => {
        toggleRecipient(recipientGroup.id, recipient, removeIfLast);

        if (draftEditorMode === DraftEditorMode.Preview) {
          resetDirtyState();
        }
      }}
      onRemoveGroup={handleDelete}
      bottomSpacing={0}
      shouldBlockRemove={isReadOnly}
    />
  );
};
